import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { getRecognitionRibbonColor } from '../../../../lib/recognitionLevelColor';
import { getAvatarStyle } from '../../../../lib/s3';

const ProfileHeader = ({ user, company }) => {
  const intl = useIntl();
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  // Get avatar style with company settings for proper background color
  const avatarStyle = getAvatarStyle(user.profile_picture, company);

  return (
    <div className="space-y-6">
      {/* Name and Headline */}
      <div>
        <h1 className="text-3xl font-bold text-succession-text-primary mb-2">
          {user.name || intl.formatMessage({ id: 'succession.profile_drawer.profile_header.unknown_user' })}
        </h1>
        {/* Professional Designations */}
        {user.designations && user.designations.length > 0 && (
          <p className="text-lg text-orange-600 font-medium mb-2">
            {user.designations.map((designation, index) => {
              const designationText = typeof designation === 'string' ? designation : designation?.name || designation?.id || '';
              return (
                <span key={designation?.id || index}>
                  {designationText}
                  {index < user.designations.length - 1 && ', '}
                </span>
              );
            })}
          </p>
        )}
        {user.headline && (
          <h2 className="text-lg text-succession-text-secondary leading-relaxed">
            {user.headline}
          </h2>
        )}
      </div>

      {/* Avatar and Bio Section */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div 
            className="w-32 h-32 rounded-full border-4 border-succession-border overflow-hidden flex items-center justify-center"
            style={{ backgroundColor: avatarStyle.backgroundColor || 'rgba(var(--succession-primary-rgb), 0.1)' }}
          >
            {user.profile_picture ? (
              <img
                src={avatarStyle.url}
                alt={user.name}
                className="w-full h-full object-cover"
                style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span class="text-2xl font-semibold text-succession-primary">${getInitials(user.name)}</span>`;
                }}
              />
            ) : (
              <span className="text-2xl font-semibold text-succession-primary">
                {getInitials(user.name)}
              </span>
            )}
          </div>
          {(company?.features || company?.feature_types || []).includes('profile_recognition_level') && user.recognition_level && (
            <div className="absolute -bottom-2 left-0 right-0 flex justify-center z-10">
              <span
                className="inline-block px-2 py-0.5 rounded text-white font-bold uppercase tracking-wide text-center leading-tight shadow-sm"
                style={{
                  maxWidth: '7.5rem',
                  fontSize: user.recognition_level.length > 10 ? '8px' : '10px',
                  backgroundColor: getRecognitionRibbonColor(user.recognition_level, company),
                }}
              >
                {user.recognition_level}
              </span>
            </div>
          )}
        </div>

        {/* Bio and Details */}
        <div className="flex-1 space-y-4">
          {/* Location */}
          {user.location && (
            <div className="flex items-center gap-2 text-succession-text-muted">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{user.location}</span>
            </div>
          )}

          {/* Bio */}
          {user.bio ? (
            <p className="text-succession-text-primary leading-relaxed whitespace-pre-line">
              {user.bio}
            </p>
          ) : (
            <p className="text-succession-text-muted italic">
              {intl.formatMessage({ id: 'succession.profile_drawer.profile_header.no_bio' })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

ProfileHeader.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    profile_picture: PropTypes.string,
    headline: PropTypes.string,
    location: PropTypes.string,
    bio: PropTypes.string,
    recognition_level: PropTypes.string,
    designations: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        })
      ])
    ),
  }).isRequired,
  company: PropTypes.shape({
    features: PropTypes.arrayOf(PropTypes.string),
    avatar_background_color: PropTypes.string,
    avatar_directory: PropTypes.string,
    primary_color: PropTypes.string,
  }),
};

export default ProfileHeader;
